<template>
  <v-container class="py-8">
    <v-row>
      <v-col cols="12" md="8" offset-md="2">
        <div class="d-flex align-center justify-space-between mb-6">
          <h1>Select Organization</h1>
          <v-btn variant="tonal" :to="{ name: 'MyOffers' }">
            <v-icon start>mdi-inbox-outline</v-icon>
            My Offers
          </v-btn>
        </div>

        <v-alert v-if="error" type="error" closable @click:close="error = null">
          {{ error }}
        </v-alert>

        <v-row class="mb-8">
          <v-col v-if="organizations.length === 0" cols="12">
            <v-card>
              <v-card-text class="text-center py-8">
                <p class="text-h6">No organizations yet</p>
                <p v-if="canCreate" class="text-body2 text-medium-emphasis">Create your organization to get started — you'll be its owner</p>
                <p v-else class="text-body2 text-medium-emphasis">
                  Ask an owner or admin to invite you. Sign in with the email address they invited and you'll be added automatically.
                </p>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col v-for="org in organizations" :key="org.id" cols="12" sm="6">
            <v-card
              :to="`/?org=${org.id}`"
              class="h-100"
            >
              <v-card-item>
                <template #title>
                  <v-btn text :to="`/?org=${org.id}`">{{ org.name }}</v-btn>
                </template>
              </v-card-item>
              <v-card-text>
                <p class="text-body2 text-medium-emphasis">
                  Created {{ formatDate(org.createdAt) }}
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-divider v-if="canCreate" class="my-6"></v-divider>

        <v-card v-if="canCreate" class="mt-8">
          <v-card-title>Create Organization</v-card-title>
          <v-card-text>
            <v-form @submit.prevent="handleCreateOrg">
              <v-text-field
                v-model="newOrgName"
                label="Organization Name"
                required
                :disabled="isCreating"
                placeholder="e.g., Amazing Productions"
              ></v-text-field>
              <v-btn
                type="submit"
                color="primary"
                :loading="isCreating"
              >
                Create Organization
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';
import { canCreateOrganization } from '@gigdriver/client';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const orgStore = useOrganizationStore();

const newOrgName = ref('');
const isCreating = ref(false);
const error = ref<string | null>(null);

const organizations = ref(orgStore.organizations);
// Single-org setup: only the first user, before any organization exists, may create one.
const canCreate = ref(false);

onMounted(async () => {
  if (authStore.currentUser) {
    await orgStore.loadUserOrganizations(authStore.currentUser.uid);
    organizations.value = orgStore.organizations;
    // Staff-only users don't pick an organization — My Offers shows open
    // positions across all of theirs.
    if (orgStore.isStaffOnly) {
      router.replace({ name: 'MyOffers' });
      return;
    }
    try {
      canCreate.value = (await canCreateOrganization()).allowed;
    } catch {
      canCreate.value = false;
    }

    // If org is in URL, select it
    if (route.query.org) {
      await orgStore.selectOrganization(route.query.org as string);
      router.push('/');
    }
  }
});

function formatDate(date: any): string {
  if (!date) return 'Unknown';
  // Handle Firestore Timestamp
  if (date.toDate) {
    return date.toDate().toLocaleDateString();
  }
  // Handle regular Date
  if (date instanceof Date) {
    return date.toLocaleDateString();
  }
  // Handle number (milliseconds)
  if (typeof date === 'number') {
    return new Date(date).toLocaleDateString();
  }
  return 'Invalid Date';
}

async function handleCreateOrg() {
  if (!authStore.currentUser) return;

  try {
    isCreating.value = true;
    error.value = null;
    const orgId = await orgStore.createNewOrganization(newOrgName.value, authStore.currentUser.uid);
    router.push(`/?org=${orgId}`);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to create organization';
  } finally {
    isCreating.value = false;
  }
}
</script>
